import { createServerFn } from '@tanstack/react-start';
import { redirect } from '@tanstack/react-router';
import {
    deleteCookie,
    getCookie,
    setCookie,
    setResponseStatus,
} from '@tanstack/react-start/server';
import { AppwriteException, ID, type Account } from 'node-appwrite';
import { z } from 'zod';

import type { StaffRole } from '@/lib/admins';
import type { ImpersonationOperator } from '@/lib/impersonation';
import { absoluteUrl } from '@/lib/site-url';
import {
    createAdminClient,
    createPublicClient,
    createSessionClient,
} from '../lib/appwrite';
import { getAdminsTeamId, resolveStaffAccess } from '../lib/admins';
import { resolveCanImpersonate } from '../lib/impersonation';
import {
    IMPERSONATE_COOKIE,
    SESSION_COOKIE,
    impersonateUserIdFromCookie,
    safeRedirectPath,
    sessionCookieOptions,
} from '../lib/session';

export { SESSION_COOKIE, safeRedirectPath } from '../lib/session';

function setSessionCookies(_id: string, secret: string) {
    setCookie(SESSION_COOKIE, secret, sessionCookieOptions());
    deleteCookie(IMPERSONATE_COOKIE);
}

/**
 * Appwrite only includes the session `secret` when created with an API key
 * (SSR pattern). https://appwrite.io/docs/products/auth/server-side-rendering
 */
function createSessionAccount(): Account {
    try {
        return createAdminClient().account;
    } catch {
        return createPublicClient().account;
    }
}

function clearSessionCookies() {
    deleteCookie(SESSION_COOKIE);
    deleteCookie(IMPERSONATE_COOKIE);
}

type AuthUser = Awaited<ReturnType<Account['get']>> & {
    impersonator?: boolean;
    impersonatorUserId?: string;
};

export type AuthState = {
    currentUser: AuthUser | null;
    canAccessAdmin: boolean;
    isFullAdmin: boolean;
    staffRole: StaffRole | null;
    canImpersonate: boolean;
    impersonation: ImpersonationOperator | null;
};

const signedOutAuth: AuthState = {
    currentUser: null,
    canAccessAdmin: false,
    isFullAdmin: false,
    staffRole: null,
    canImpersonate: false,
    impersonation: null,
};

function throwAppwriteError(error: unknown): never {
    const appwriteError = error as AppwriteException;
    setResponseStatus(appwriteError.code || 500);
    throw {
        message: appwriteError.message || 'Something went wrong',
        status: appwriteError.code || 500,
    };
}

const credentialsSchema = z.object({
    email: z.email('Please enter a valid email address'),
    password: z.string().min(1, 'Password is required'),
    redirect: z.string().optional(),
});

export const signInFn = createServerFn({ method: 'POST' })
    .validator(credentialsSchema)
    .handler(async ({ data }) => {
        const { email, password, redirect: redirectUrl } = data;

        try {
            const session =
                await createSessionAccount().createEmailPasswordSession({
                    email,
                    password,
                });
            if (!session.secret) {
                throw new Error(
                    'Appwrite returned no session secret - set APPWRITE_API_KEY for SSR auth',
                );
            }
            setSessionCookies(session.$id, session.secret);
        } catch (error) {
            throwAppwriteError(error);
        }

        throw redirect({ to: safeRedirectPath(redirectUrl) });
    });

const signUpSchema = z.object({
    name: z.string().trim().min(1, 'Name is required').max(128),
    email: z.email('Please enter a valid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    redirect: z.string().optional(),
});

export const signUpFn = createServerFn({ method: 'POST' })
    .validator(signUpSchema)
    .handler(async ({ data }) => {
        const { name, email, password, redirect: redirectUrl } = data;

        try {
            const account = createSessionAccount();
            await account.create({
                userId: ID.unique(),
                email,
                password,
                name,
            });
            const session = await account.createEmailPasswordSession({
                email,
                password,
            });
            if (!session.secret) {
                throw new Error(
                    'Appwrite returned no session secret - set APPWRITE_API_KEY for SSR auth',
                );
            }
            setSessionCookies(session.$id, session.secret);
        } catch (error) {
            throwAppwriteError(error);
        }

        throw redirect({ to: safeRedirectPath(redirectUrl) });
    });

const recoveryRequestSchema = z.object({
    email: z.email('Please enter a valid email address'),
});

export const requestRecoveryFn = createServerFn({ method: 'POST' })
    .validator(recoveryRequestSchema)
    .handler(async ({ data }) => {
        try {
            await createPublicClient().account.createRecovery({
                email: data.email,
                url: absoluteUrl('/recover/reset'),
            });
        } catch (error) {
            throwAppwriteError(error);
        }

        return { ok: true as const };
    });

const recoveryCompleteSchema = z.object({
    userId: z.string().min(1),
    secret: z.string().min(1),
    password: z.string().min(8, 'Password must be at least 8 characters'),
});

export const completeRecoveryFn = createServerFn({ method: 'POST' })
    .validator(recoveryCompleteSchema)
    .handler(async ({ data }) => {
        try {
            await createPublicClient().account.updateRecovery({
                userId: data.userId,
                secret: data.secret,
                password: data.password,
            });
        } catch (error) {
            throwAppwriteError(error);
        }

        return { ok: true as const };
    });

export const signOutFn = createServerFn({ method: 'POST' }).handler(
    async () => {
        const sessionSecret = getCookie(SESSION_COOKIE);
        if (sessionSecret && !sessionSecret.startsWith('jwt:')) {
            try {
                const client = await createSessionClient(sessionSecret, {
                    asOperator: true,
                });
                await client.account.deleteSession({ sessionId: 'current' });
            } catch {
                // Session may already be invalid - still clear cookies.
            }
        }

        clearSessionCookies();
        throw redirect({ to: '/sign-in' });
    },
);

export const authMiddleware = createServerFn({ method: 'GET' }).handler(
    async (): Promise<AuthState> => {
        const session = getCookie(SESSION_COOKIE);
        if (!session) {
            return signedOutAuth;
        }

        try {
            const operatorClient = await createSessionClient(session, {
                asOperator: true,
            });
            const operator = (await operatorClient.account.get()) as AuthUser;
            const [staff, canImpersonate] = await Promise.all([
                resolveStaffAccess(operator.$id),
                resolveCanImpersonate(
                    operator.$id,
                    operator,
                    operatorClient.users,
                ),
            ]);
            operator.impersonator = canImpersonate;
            const targetId = impersonateUserIdFromCookie(
                getCookie(IMPERSONATE_COOKIE),
            );

            const operatorAuth = {
                canAccessAdmin: staff.canAccessAdmin,
                isFullAdmin: staff.isFullAdmin,
                staffRole: staff.staffRole,
                canImpersonate,
            };

            if (!targetId) {
                return {
                    currentUser: operator,
                    impersonation: null,
                    ...operatorAuth,
                };
            }

            if (!canImpersonate) {
                deleteCookie(IMPERSONATE_COOKIE);
                return {
                    currentUser: operator,
                    impersonation: null,
                    ...operatorAuth,
                };
            }

            try {
                const impersonatedClient = await createSessionClient(session, {
                    impersonateUserId: targetId,
                });
                const currentUser =
                    (await impersonatedClient.account.get()) as AuthUser;
                if (currentUser.$id === operator.$id) {
                    deleteCookie(IMPERSONATE_COOKIE);
                    return {
                        currentUser: operator,
                        impersonation: null,
                        ...operatorAuth,
                    };
                }
                return {
                    currentUser,
                    impersonation: {
                        $id: operator.$id,
                        email: operator.email,
                        name: operator.name,
                    },
                    ...operatorAuth,
                };
            } catch {
                deleteCookie(IMPERSONATE_COOKIE);
                return {
                    currentUser: operator,
                    impersonation: null,
                    ...operatorAuth,
                };
            }
        } catch {
            clearSessionCookies();
            return signedOutAuth;
        }
    },
);

export const getCurrentUser = createServerFn({ method: 'GET' }).handler(
    async () => {
        const { currentUser } = await authMiddleware();
        return currentUser;
    },
);

const prefsPatchSchema = z.object({
    prefs: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
});

/** Merge keys into the signed-in staff user's Appwrite account prefs. */
export const updateStaffPrefsFn = createServerFn({ method: 'POST' })
    .validator(prefsPatchSchema)
    .handler(async ({ data }) => {
        const auth = await requireStaffAccess();
        const userId = auth.currentUser.$id;
        const { users } = createAdminClient();
        const user = await users.get({ userId });
        return users.updatePrefs({
            userId,
            prefs: {
                ...(user.prefs ?? {}),
                ...data.prefs,
            },
        });
    });

/** Require a signed-in user who belongs to the Appwrite admins team. */
export async function requireStaffAccess() {
    const auth = await authMiddleware();
    if (!auth.currentUser) {
        throw redirect({ to: '/sign-in', search: { redirect: '/admin' } });
    }
    if (!auth.canAccessAdmin) {
        throw redirect({ to: '/admin/forbidden' });
    }
    return auth;
}

/** Require a signed-in user with the full admin (owner) team role. */
export async function requireAdminAccess() {
    const auth = await requireStaffAccess();
    if (!auth.isFullAdmin) {
        throw redirect({ to: '/admin/forbidden' });
    }
    return auth;
}

const staffInviteQuerySchema = z.object({
    userId: z.string().min(1),
    secret: z.string().min(1),
    membershipId: z.string().min(1),
    teamId: z.string().min(1).optional(),
});

export const getStaffInviteFn = createServerFn({ method: 'GET' })
    .validator(staffInviteQuerySchema)
    .handler(async ({ data }) => {
        const teamId = getAdminsTeamId();
        if (data.teamId && data.teamId !== teamId) {
            throw {
                message: 'ההזמנה אינה שייכת לצוות הזה.',
                status: 400,
            };
        }
        try {
            const membership = await createAdminClient().teams.getMembership({
                teamId,
                membershipId: data.membershipId,
            });
            if (membership.userId !== data.userId) {
                throw {
                    message: 'ההזמנה אינה תקינה.',
                    status: 400,
                };
            }
            const user = await createAdminClient().users.get({
                userId: data.userId,
            });
            return {
                email: user.email,
                confirm: membership.confirm,
                needsPassword: !user.passwordUpdate,
            };
        } catch (error) {
            if (
                error &&
                typeof error === 'object' &&
                'message' in error &&
                'status' in error
            ) {
                throw error;
            }
            throwAppwriteError(error);
        }
    });

const acceptStaffInviteSchema = staffInviteQuerySchema.extend({
    password: z.string().min(8).optional(),
});

export const acceptStaffInviteFn = createServerFn({ method: 'POST' })
    .validator(acceptStaffInviteSchema)
    .handler(async ({ data }) => {
        const teamId = getAdminsTeamId();
        if (data.teamId && data.teamId !== teamId) {
            throw {
                message: 'ההזמנה אינה שייכת לצוות הזה.',
                status: 400,
            };
        }

        try {
            const admin = createAdminClient();
            const user = await admin.users.get({ userId: data.userId });
            const needsPassword = !user.passwordUpdate;
            if (needsPassword && !data.password) {
                throw {
                    message: 'נא לבחור סיסמה כדי להשלים את ההזמנה.',
                    status: 400,
                };
            }

            await createPublicClient().teams.updateMembershipStatus({
                teamId,
                membershipId: data.membershipId,
                userId: data.userId,
                secret: data.secret,
            });

            if (data.password) {
                await admin.users.updatePassword({
                    userId: data.userId,
                    password: data.password,
                });
            }

            const session = await admin.users.createSession({
                userId: data.userId,
            });
            if (!session.secret) {
                throw new Error(
                    'Appwrite returned no session secret - set APPWRITE_API_KEY for SSR auth',
                );
            }
            setSessionCookies(session.$id, session.secret);
        } catch (error) {
            if (
                error &&
                typeof error === 'object' &&
                'message' in error &&
                'status' in error
            ) {
                throw error;
            }
            throwAppwriteError(error);
        }

        throw redirect({ to: '/admin' });
    });
